import React, { useState, useEffect, useRef } from 'react';
import { executiveFormationService } from '../../services/executiveFormationService';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Checkbox } from 'primereact/checkbox';
import { HiPlus, HiTrash, HiPencil, HiSave, HiClipboardList, HiChevronLeft, HiDownload, HiAcademicCap, HiCog } from 'react-icons/hi';
import './styles/ExecutiveFormationsManagement.css';

function ExecutiveFormationsManagement({ editionYear }) {
  const [settings, setSettings] = useState({
    hero_title: 'Formations Executive & Certifications',
    hero_subtitle: 'Développez vos compétences avec nos programmes d’excellence executive',
    intro_text: 'Nos formations executive combinent expertise académique et compétences pratiques.',
    is_visible: true
  });
  const [certificates, setCertificates] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('registrations'); // 'registrations', 'certificates', 'settings'
  const [showCertDialog, setShowCertDialog] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [isAddingCert, setIsAddingCert] = useState(false);
  const toast = useRef(null);

  useEffect(() => {
    loadData();
  }, [editionYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const settingsData = await executiveFormationService.getSettings(editionYear);
      setSettings(settingsData);

      const certsData = await executiveFormationService.getCertificates(editionYear);
      setCertificates(certsData);

      const regList = await executiveFormationService.getRegistrations(editionYear);
      setRegistrations(regList);
    } catch (error) {
      console.error('Error loading executive formations data:', error);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Impossible de charger les données' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await executiveFormationService.updateSettings(settings);
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Paramètres mis à jour' });
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la sauvegarde' });
      }
    }
  };

  const handleAddCertificate = () => {
    setEditingCert({
      title: '',
      description: '',
      duration: '40 Heures',
      price: 0,
      display_order: certificates.length + 1,
      is_active: true,
      edition_id: settings.edition_id
    });
    setIsAddingCert(true);
    setShowCertDialog(true);
  };

  const handleEditCertificate = (cert) => {
    setEditingCert({ ...cert });
    setIsAddingCert(false);
    setShowCertDialog(true);
  };

  const handleSaveCertificate = async () => {
    try {
      await executiveFormationService.saveCertificate(editingCert);
      await loadData();
      setShowCertDialog(false);
      setEditingCert(null);
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Certificat enregistré' });
      }
    } catch (error) {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de l\'enregistrement' });
      }
    }
  };

  const handleDeleteCertificate = async (cert) => {
    if (window.confirm(`Supprimer le certificat "${cert.title}" ?`)) {
      try {
        await executiveFormationService.deleteCertificate(cert.id);
        await loadData();
        if (toast.current) {
          toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Certificat supprimé' });
        }
      } catch (error) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la suppression' });
        }
      }
    }
  };

  const handleDeleteRegistration = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette inscription executive ?')) {
      try {
        await executiveFormationService.deleteRegistration(id);
        await loadData();
        if (toast.current) {
          toast.current.show({ severity: 'success', summary: 'Succès', detail: 'Inscription supprimée' });
        }
      } catch (error) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Erreur', detail: 'Échec de la suppression' });
        }
      }
    }
  };

  const exportCSV = () => {
    if (!registrations || registrations.length === 0) return;
    const headers = ["Date", "Nom", "Prénom", "Email", "Téléphone", "Certificat"];
    const rows = registrations.map(r => [
      new Date(r.created_at).toLocaleDateString('fr-FR'),
      `"${r.nom || ''}"`,
      `"${r.prenom || ''}"`,
      `"${r.email || ''}"`,
      `"${r.telephone || ''}"`,
      `"${r.certificate_name || ''}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `inscriptions_executive_${editionYear || '2025'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const dateBodyTemplate = (rowData) => {
    return new Date(rowData.created_at).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusBodyTemplate = (rowData) => {
    return <Tag value={rowData.is_active ? 'Actif' : 'Masqué'} severity={rowData.is_active ? 'success' : 'warning'} />;
  };

  const certActionsTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button icon={<HiPencil />} className="p-button-rounded p-button-info p-button-outlined" onClick={() => handleEditCertificate(rowData)} title="Éditer" />
        <Button icon={<HiTrash />} className="p-button-rounded p-button-danger p-button-outlined" onClick={() => handleDeleteCertificate(rowData)} title="Supprimer" />
      </div>
    );
  };

  const regActionsTemplate = (rowData) => {
    return (
      <Button icon={<HiTrash />} className="p-button-rounded p-button-danger p-button-outlined" onClick={() => handleDeleteRegistration(rowData.id)} title="Supprimer l'inscription" />
    );
  };

  if (loading && !showCertDialog) return <div className="p-4 text-center">Chargement des données Executive...</div>;

  return (
    <div className="executive-formations-management admin-section">
      <Toast ref={toast} />

      <div className="flex justify-between items-center mb-6 header-admin-bar">
        <h2 className="font-bold flex items-center gap-2" style={{ fontSize: '1.5rem', color: '#1a202c' }}>
          <HiAcademicCap style={{ color: '#002b5b' }} /> Formations Executive <span className="text-red-600">[{editionYear || '2025'}]</span>
        </h2>
        
        <div className="flex gap-2">
          <Button 
            label="Inscriptions Enregistrées" 
            icon={<HiClipboardList className="mr-1" />} 
            className={view === 'registrations' ? "p-button-danger" : "p-button-secondary p-button-outlined"}
            onClick={() => setView('registrations')} 
          />
          <Button 
            label="Certificats & Offres" 
            icon={<HiAcademicCap className="mr-1" />} 
            className={view === 'certificates' ? "p-button-danger" : "p-button-secondary p-button-outlined"}
            onClick={() => setView('certificates')} 
          />
          <Button 
            label="Paramètres Page" 
            icon={<HiCog className="mr-1" />} 
            className={view === 'settings' ? "p-button-danger" : "p-button-secondary p-button-outlined"}
            onClick={() => setView('settings')} 
          />
        </div>
      </div>

      {/* Registrations List View */}
      {view === 'registrations' && (
        <div className="mgmt-card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="mgmt-card-title">Inscriptions Executive & Certifications ({registrations.length})</h3>
            <Button 
              label="Exporter CSV" 
              icon={<HiDownload className="mr-2" />} 
              className="p-button-success p-button-outlined"
              onClick={exportCSV} 
              disabled={registrations.length === 0}
            />
          </div>

          <DataTable 
            value={registrations} 
            responsiveLayout="scroll" 
            className="p-datatable-sm" 
            paginator 
            rows={10} 
            stripedRows 
            emptyMessage="Aucune inscription enregistrée pour le moment."
            rowHover
          >
            <Column field="created_at" header="Date d'inscription" body={dateBodyTemplate} sortable style={{ width: '13rem' }} />
            <Column header="Participant" body={(r) => <span className="font-semibold">{r.nom} {r.prenom}</span>} sortable />
            <Column field="email" header="Email" sortable />
            <Column field="telephone" header="Téléphone" sortable />
            <Column field="certificate_name" header="Certificat Choisi" sortable />
            <Column header="Actions" body={regActionsTemplate} style={{ width: '6rem' }} />
          </DataTable>
        </div>
      )}

      {/* Certificates View */}
      {view === 'certificates' && (
        <div className="mgmt-card">
          <div className="flex justify-between items-center mb-4">
            <h3 className="mgmt-card-title">Catalog des Certificats et Formations</h3>
            <Button 
              label="Ajouter un Certificat" 
              icon={<HiPlus className="mr-2" />} 
              className="p-button-danger"
              onClick={handleAddCertificate} 
            />
          </div>

          <DataTable 
            value={certificates} 
            responsiveLayout="scroll" 
            className="p-datatable-sm" 
            stripedRows 
            emptyMessage="Aucun certificat défini."
            rowHover
          >
            <Column field="display_order" header="Ordre" style={{ width: '5rem' }} sortable />
            <Column field="title" header="Intitulé du Certificat" sortable />
            <Column field="duration" header="Durée" style={{ width: '8rem' }} />
            <Column field="is_active" header="État" body={statusBodyTemplate} style={{ width: '7rem' }} />
            <Column header="Actions" body={certActionsTemplate} style={{ width: '8rem' }} />
          </DataTable>
        </div>
      )}

      {/* Page Settings View */}
      {view === 'settings' && (
        <div className="mgmt-card max-w-3xl">
          <h3 className="mgmt-card-title">Configuration de la Page Executive</h3>
          <div className="flex flex-column gap-3 mt-3">
            <div className="flex flex-column gap-2">
              <label className="font-semibold text-gray-700">Titre Principal de la Bannières</label>
              <InputText 
                value={settings.hero_title || ''} 
                onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })} 
                className="w-full"
              />
            </div>
            
            <div className="flex flex-column gap-2">
              <label className="font-semibold text-gray-700">Sous-titre de la Bannière</label>
              <InputText 
                value={settings.hero_subtitle || ''} 
                onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })} 
                className="w-full"
              />
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-semibold text-gray-700">Texte d'Introduction</label>
              <InputTextarea 
                value={settings.intro_text || ''} 
                onChange={(e) => setSettings({ ...settings, intro_text: e.target.value })} 
                rows={3}
                className="w-full"
              />
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Checkbox 
                inputId="isVisible" 
                checked={settings.is_visible} 
                onChange={(e) => setSettings({ ...settings, is_visible: e.checked })} 
              />
              <label htmlFor="isVisible" className="cursor-pointer font-medium">Afficher la page sur le site public</label>
            </div>

            <div className="flex justify-end mt-4">
              <Button 
                label="Enregistrer les modifications" 
                icon={<HiSave className="mr-2" />} 
                onClick={handleSaveSettings} 
                className="p-button-danger bg-red-600 border-none px-4" 
              />
            </div>
          </div>
        </div>
      )}

      {/* Dialog for Adding/Editing Certificate */}
      <Dialog 
        header={isAddingCert ? "✨ Nouveau Certificat" : "📝 Éditer le Certificat"} 
        visible={showCertDialog} 
        style={{ width: '50vw' }} 
        modal 
        onHide={() => setShowCertDialog(false)}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Annuler" icon={<HiChevronLeft className="mr-1" />} className="p-button-text p-button-secondary" onClick={() => setShowCertDialog(false)} />
            <Button label="Enregistrer" icon={<HiSave className="mr-1" />} className="p-button-primary" onClick={handleSaveCertificate} />
          </div>
        }
      >
        {editingCert && (
          <div className="flex flex-column gap-3 mt-2">
            <div className="flex flex-column gap-2">
              <label className="font-semibold">Titre du Certificat *</label>
              <InputText value={editingCert.title} onChange={(e) => setEditingCert({ ...editingCert, title: e.target.value })} className="w-full" />
            </div>

            <div className="flex flex-column gap-2">
              <label className="font-semibold">Description courte</label>
              <InputTextarea value={editingCert.description || ''} onChange={(e) => setEditingCert({ ...editingCert, description: e.target.value })} rows={3} className="w-full" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-column gap-2">
                <label className="font-semibold">Durée indicative (ex: 60 Heures)</label>
                <InputText value={editingCert.duration || ''} onChange={(e) => setEditingCert({ ...editingCert, duration: e.target.value })} className="w-full" />
              </div>
              <div className="flex flex-column gap-2">
                <label className="font-semibold">Ordre d'affichage</label>
                <InputNumber value={editingCert.display_order} onValueChange={(e) => setEditingCert({ ...editingCert, display_order: e.value })} className="w-full" />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Checkbox inputId="certActive" checked={editingCert.is_active} onChange={(e) => setEditingCert({ ...editingCert, is_active: e.checked })} />
              <label htmlFor="certActive" className="cursor-pointer font-medium">Actif (Proposé dans le formulaire)</label>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

export default ExecutiveFormationsManagement;
